import {Injectable} from '@angular/core';
import {AngularFirestore, CollectionReference, DocumentData, Query} from '@angular/fire/compat/firestore';
import {lastValueFrom, Observable, of} from 'rxjs';
import {FirebaseGroupModel} from '../../models/firebase-group.model';
import {FirebaseChatNotificationDevicesModel} from '../../models/firebase-chat-notification-devices.model';
import {AngularFireAuth} from '@angular/fire/compat/auth';
import {catchError, map} from 'rxjs/operators';
import {ChatGroupService} from '../chat-group/chat-group.service';
import {WhereFilterOp} from '@angular/fire/firestore';
import {ChatGroupModel} from "../../models/chat-group.model";
import {FilterableMessageType} from "../../enums/filterable-message-type.enum";
import {SharableEntity, SharedMessageModel} from "../../models/shared-entity.model";
import {ChatMessageActionType, ChatMessageType} from "../../enums/chat-message-type.enum";
import {BaseUserService} from "../../../shared/services/base-user/base-user.service";
import {TagModel} from "../../../shared/tags/models/tag.model";
import {UtilsService} from "../../../shared/services/utils/utils.service";
import {FilterableObj} from "../../../shared/models/filterable-obj.model";
import {EntityDataState} from "../../enums/message-state.enum";
import {FirestoreService} from "../../../shared/services/firestore/firestore.service";
import {FirestoreCollectionEnum} from "../../../shared/enums/firestore-collection.enum";
import {NotificationType} from "../../../shared/enums/notification-type.enum";
import {ExclusionFilterModel} from "../../models/exclusion-filter.model";
import {DataNotification} from "../../../shared/services/firebase-notification/firebase-notification.service";
import {NotificationEntityName} from "../../../shared/enums/notification-entity-name.enum";

@Injectable({
  providedIn: 'root'
})
export class ChatService extends FirestoreService {

  private currentChatGroupUID: string;
  defaultLimit = 10;

  constructor(
    protected readonly afs: AngularFirestore,
    protected readonly angularFireAuth: AngularFireAuth,
    protected readonly baseUserService: BaseUserService,
    private chatGroupService: ChatGroupService,
    private readonly utilsService: UtilsService
  ) {
    super(afs, angularFireAuth, baseUserService);
  }

  setCurrentChatGroupUID(uid) {
    this.currentChatGroupUID = uid;
  }

  getCurrentChatGroupUID() {
    return this.currentChatGroupUID;
  }

  getMessagesWhere(limit?: number, startAfter?: any, whereClauses?: {
    field: string,
    operator: WhereFilterOp,
    value: any
  }[], defaultSort?: boolean) {
    return this.afs
      .collectionGroup(FirestoreCollectionEnum.messages, (query: Query<any>) => {
        return this.queryFirebase(query, limit, startAfter, whereClauses, defaultSort);
      })
      .get()
      .toPromise();
  }

  chatMessagesWhere(groupId: string, limit?: number, startAfter?: any, whereClauses?: {
    field: string,
    operator: WhereFilterOp,
    value: any
  }[], defaultSort?: boolean, customSort?: string[]) {
    return this.afs
      .collection(FirestoreCollectionEnum.chat)
      .doc(groupId)
      .collection(FirestoreCollectionEnum.messages, (ref: CollectionReference<DocumentData>) => {
        return this.queryFirebase(ref, limit, startAfter, whereClauses, defaultSort, customSort);
      })
  }

  async getChatMessages(groupId: string, limit?: number, startAfter?: any, retrying?: boolean,
                  whereClauses?: {
                    field: string,
                    operator: WhereFilterOp,
                    value: any
                  }[], defaultSort?: boolean, customSort?: string[]): Promise<any> {
    console.log(await this.baseUserService.loggedUser())
    return (lastValueFrom(
      this.chatMessagesWhere(groupId, limit, startAfter, whereClauses, defaultSort, customSort)
        .get()
        .pipe(
          map(result => result),
          catchError(async err => {
            // if request fails I try signout the user and sign it back in (just once)
            if (!retrying) {
              const user = await this.baseUserService.loggedUser();
              await this.logout();
              await this.signInWithEmailAndPassword(user.email, user.chatToken);
              let chatInfo = await this.chatGroupService.getChatInfo();
              return await this.getChatMessages(groupId, limit, startAfter, true, whereClauses, defaultSort, customSort);
            }
            return of('error', err);
          })
        )
    ))
  }

  listenChatMessages(groupId: string, limit?: number, startAfter?: any, retrying?: boolean,
                     whereClauses?: {
                       field: string,
                       operator: WhereFilterOp,
                       value: any
                     }[], defaultSort?: boolean): Observable<any> {
    return this.chatMessagesWhere(groupId, limit, startAfter, whereClauses, defaultSort)
      .valueChanges({idField: 'id'});
  }

  // snapshot (groupId: string, limit?: number, startAfter?: any, retrying?: boolean,
  //           whereClauses?: {field: string, operator: WhereFilterOp, value: any}[]) {
  //   return this.chatMessagesWhere(groupId, limit, startAfter, whereClauses)
  //     .snapshotChanges()
  //     .pipe(
  //       map(actions => {
  //         actions.map(a => a.payload.doc.data());
  //         console.log(actions)
  //       })
  //     );
  // }

  getMessageById(id: string, groupId: string) {
    return this.afs.collection(FirestoreCollectionEnum.chat).doc(groupId).collection(FirestoreCollectionEnum.messages).doc(id).get().toPromise();
  }

  async addMessage(groupId: string, message: SharedMessageModel): Promise<any> {
    await this.sendMessageNotification(groupId, message);
    return this.afs.collection(FirestoreCollectionEnum.chat).doc(groupId).collection(FirestoreCollectionEnum.messages).add(message);
  }

  updateMessage(groupId: string, messageId: string, message) {
    return this.afs.collection(FirestoreCollectionEnum.chat).doc(groupId).collection(FirestoreCollectionEnum.messages).doc(messageId).update(message);
  }

  async removeEntityDataSharing(message: SharedMessageModel) {
    let text;
    if(message.content.text) {
      text = JSON.parse(JSON.stringify(message.content.text));
    }

    message.content = {};

    if(text) {
      message.content.text = text;
    }
    message.contentState = {};
    message.contentState.entityData = EntityDataState.SHARE_REMOVED;
    await this.updateMessage(message.chatGroupId, message.id, message);
    return message;
  }

  // addTags(messageId: string, tags: TagModel[]) {
  //   return this.afs.collection('messages').doc(messageId).collection('tags').add(tags);
  // }

  addFirebaseGroup(group: FirebaseGroupModel): Promise<any> {
    return this.afs.collection(FirestoreCollectionEnum.chat).add(group);
  }

  async getFirebaseGroup(groupId: string): Promise<any> {
    return await this.afs.collection<FirebaseGroupModel>(FirestoreCollectionEnum.chat).doc(groupId).get().toPromise();
  }

  async updateFirebaseGroup(groupId: string, group: FirebaseGroupModel): Promise<void> {
    await this.sendChatGroupNotificationsIfNeeded(groupId, group);

    return await this.afs.collection<FirebaseGroupModel>(FirestoreCollectionEnum.chat).doc(groupId).update(group);
  }

  getFirebaseNotificationDevices(userUID: string): Promise<any> {
    return this.afs.collection(FirestoreCollectionEnum.chatNotificationDevices).doc(userUID).get().toPromise();
  }

  addFirebaseNotificationDevices(userUID: string, notificationDevices: FirebaseChatNotificationDevicesModel): Promise<any> {
    return this.afs.collection(FirestoreCollectionEnum.chatNotificationDevices).doc(userUID).set(notificationDevices);
  }

  updateFirebaseNotificationDevices(userUID: string, notificationDevices: FirebaseChatNotificationDevicesModel): Promise<any> {
    return this.afs.collection(FirestoreCollectionEnum.chatNotificationDevices).doc(userUID).update(notificationDevices);
  }

  async removeUserFromFirebaseGroup(userUid: string, group: ChatGroupModel, firebaseGroup: FirebaseGroupModel) {
    this.utilsService.removeElementFromArray(userUid, firebaseGroup.allowedReadUsers);
    this.utilsService.removeElementFromArray(userUid, firebaseGroup.allowedWriteUsers);
    this.utilsService.removeElementFromArray(userUid, firebaseGroup.admins);

    if (firebaseGroup.owner === userUid) {
      firebaseGroup.owner = '';
    }

    await this.updateFirebaseGroup(group.uid, firebaseGroup);
  }

  baseMessageData(message: any) {

    return {
      ...message,
      createdAt: this.createFirebaseDateTime(),
    }
  }

  mapTagsForFirebase(tags: TagModel[]) {
    const tagsForFirebase = [];
    for(const tag of tags) {
      tagsForFirebase.push(this.mapTagForFirebase(tag));
    }
    return tagsForFirebase;
  }

  mapTagForFirebase(tag: TagModel) {
    return {
      id: tag.id,
      label: tag.label,
      icon: tag.icon,
      color: tag.color
    }
  }

  chatGroupMessageData(message: any, group: { chatGroup: ChatGroupModel, firebaseGroup: any }) {
    return {
      ...message,
      chatGroupId: group.chatGroup.uid,
      chatGroupName: group.chatGroup.groupName,
    }
  }

  typeFilters(selectedType: FilterableObj, filterWhereClauses: {
    field: string,
    operator: WhereFilterOp,
    value: any
  }[]): { field: string, operator: WhereFilterOp, value: any }[] {
    if (selectedType) {
      let clause;
      if (selectedType.sharableEntity == SharableEntity.CCO) {
        clause = {field: 'content.entityData.type', operator: '==', value: selectedType.key.toLowerCase()};
      } else if (selectedType.key == FilterableMessageType.EQUIPMENT || selectedType.key == FilterableMessageType.DOCUMENT ||
      selectedType.key == FilterableMessageType.ACTIVITY) {
        clause = {field: 'content.entityName', operator: '==', value: selectedType.sharableEntity}
      } else if (selectedType.key == FilterableMessageType.TEXT) {
        clause = {field: 'type', operator: '==', value: ChatMessageActionType.new_text}
      }

      if (clause) {
        filterWhereClauses.push(clause);
      }
    }
    return filterWhereClauses;
  }

  private async sendChatGroupNotificationsIfNeeded(groupId: string, newFirebaseGroup: FirebaseGroupModel) {
    const oldFirebaseGroup: FirebaseGroupModel = (await this.getFirebaseGroup(groupId)).data();
    const group = await this.chatGroupService.getChatGroupDataByUid(groupId);

    for (const type of Object.values(NotificationType)) {
      await this.sendChatGroupNotificationByTypeIfNeeded(type, newFirebaseGroup, oldFirebaseGroup, group);
    }
  }

  private async sendChatGroupNotificationByTypeIfNeeded(type: NotificationType,
                                               newFirebaseGroup: FirebaseGroupModel,
                                               oldFirebaseGroup: FirebaseGroupModel,
                                               group: ChatGroupModel) {
    switch (type) {
      case NotificationType.PROMOTED_TO_OWNER:
        if (newFirebaseGroup.owner != oldFirebaseGroup.owner) {
          await this.chatGroupService.sendChatGroupNotification(type, group, [newFirebaseGroup.owner]);
        }

        break;
      case NotificationType.DEMOTED_FROM_ADMIN:
        if (newFirebaseGroup.owner === oldFirebaseGroup.owner) {
          await this.sendChatGroupNotificationIfValuesHaveChanged(type, newFirebaseGroup, oldFirebaseGroup, group);
        }

        break;
      default:
        await this.sendChatGroupNotificationIfValuesHaveChanged(type, newFirebaseGroup, oldFirebaseGroup, group);
    }
  }

  private async sendChatGroupNotificationIfValuesHaveChanged(type: NotificationType,
                                                             newFirebaseGroup: FirebaseGroupModel,
                                                             oldFirebaseGroup: FirebaseGroupModel,
                                                             group: ChatGroupModel) {
    const exclusionFilter = this.getExclusionFilterByNotificationType(type, newFirebaseGroup, oldFirebaseGroup);

    if (exclusionFilter) {
      const filteredArray = this.excludeItemsFromStringArray(exclusionFilter);

      if (!!filteredArray?.length) {
        await this.chatGroupService.sendChatGroupNotification(type, group, filteredArray);
      }
    }
  }

  private getExclusionFilterByNotificationType(type: NotificationType, newFirebaseGroup: FirebaseGroupModel, oldFireBaseGroup: FirebaseGroupModel) {
    switch (type) {
      case NotificationType.REMOVED_USER_FROM_CHAT:
        return {
          allItems: oldFireBaseGroup.allowedReadUsers,
          itemsToExclude: newFirebaseGroup.allowedReadUsers
        };
      case NotificationType.ADDED_USER_TO_CHAT:
        return {
          allItems: newFirebaseGroup.allowedReadUsers,
          itemsToExclude: oldFireBaseGroup.allowedReadUsers
        };
      case NotificationType.GRANTED_WRITE_PERMISSIONS:
        return {
          allItems: newFirebaseGroup.allowedWriteUsers,
          itemsToExclude: oldFireBaseGroup.allowedWriteUsers
        };
      case NotificationType.REVOKED_WRITE_PERMISSIONS:
        return {
          allItems: oldFireBaseGroup.allowedWriteUsers,
          itemsToExclude: newFirebaseGroup.allowedWriteUsers
        };
      case NotificationType.DEMOTED_FROM_ADMIN:
        return {
          allItems: oldFireBaseGroup.admins,
          itemsToExclude: newFirebaseGroup.admins
        };
      case NotificationType.PROMOTED_TO_ADMIN:
        return {
          allItems: newFirebaseGroup.admins,
          itemsToExclude: oldFireBaseGroup.admins
        };
      default:
        return null;
    }
  }

  private excludeItemsFromStringArray(exclusionFilter: ExclusionFilterModel) {
    return exclusionFilter.allItems.filter(item => !exclusionFilter.itemsToExclude?.includes(item));
  }

  private getNotificationTypeByMessageType(type: ChatMessageActionType): NotificationType {
    switch (type) {
      case ChatMessageActionType.new_text:
        return NotificationType.NEW_TEXT;
      case ChatMessageActionType.new_journal:
        return NotificationType.NEW_JOURNAL;
      case ChatMessageActionType.new_photo_gallery:
        return NotificationType.NEW_PHOTO_GALLERY;
      case ChatMessageActionType.new_survey:
        return NotificationType.NEW_SURVEY;
      case ChatMessageActionType.new_construction_document:
        return NotificationType.NEW_CONSTRUCTION_DOCUMENT;
      case ChatMessageActionType.new_equipment:
        return NotificationType.NEW_EQUIPMENT;
      case ChatMessageActionType.share_equipment:
        return NotificationType.SHARE_EQUIPMENT;
      case ChatMessageActionType.share_actor_document:
        return NotificationType.SHARE_ACTOR_DOCUMENT;
      case ChatMessageActionType.new_activity:
        return NotificationType.NEW_ACTIVITY;
    }
  }

  private async sendMessageNotification(groupId: string, message: SharedMessageModel) {
    const chatGroup = (await this.getFirebaseGroup(groupId))?.data();
    const notificationType = this.getNotificationTypeByMessageType(message.type);
    const recipientUsersUIDs = chatGroup.allowedReadUsers;
    const link = this.chatGroupService.getNotificationLinkByConstructionId(message.constructionId);
    const queryParams = this.chatGroupService.getNotificationParamsByGroupUID(groupId);
    const notificationMessage = this.getNotificationMessage(message);
    const dataNotification: DataNotification = {
      type: notificationType,
      entityName: NotificationEntityName.MESSAGE,
      entityId: null,
      entityData: message,
    };

    await this.chatGroupService.sendNotifications(
      message.createdByUsername,
      link,
      queryParams,
      notificationMessage,
      recipientUsersUIDs,
      dataNotification
    );
  }

  private getNotificationMessage(message: SharedMessageModel) {
    const text = message.content.text ?? '';
    const entityDescription = this.getMessageEntityDescriptionByMessageType(message.type);

    if (!entityDescription) {
      return text;
    }

    return `${text}\n${entityDescription} condiviso\n${message.content.entityData?.title}`;
  }

  private getMessageEntityDescriptionByMessageType(messageType: ChatMessageActionType) {
    switch (messageType) {
      case ChatMessageActionType.new_journal:
        return `Nuovo diario`;
      case ChatMessageActionType.new_photo_gallery:
        return `Nuovo fascicolo fotografico`
      case ChatMessageActionType.new_survey:
        return `Nuovo Verbale`;
      case ChatMessageActionType.new_construction_document:
        return `Nuovo documento di cantiere`;
      case ChatMessageActionType.new_equipment:
        return `Nuovo mezzo di cantiere`;
      case ChatMessageActionType.share_equipment:
        return `Mezzo privato`;
      case ChatMessageActionType.share_actor_document:
        return ChatMessageType.share_actor_document;
      default:
        return null;
    }
  }
}
